import type { ActionConfig } from './types';
import { workorderCreateRoute } from './workorderCreateRoute';

export const clientActions: ActionConfig[] = [
  // =============================================================================
  // NAVIGATE APP ACTIONS
  // =============================================================================
  {
    id: 'client-log-service',
    name: 'Log Service',
    icon: 'clipboard-list',
    table: 'clients',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 1,
    config: {
      route: '/service-logs/new',
      params: { clientId: 'key' },
    },
  },
  {
    id: 'client-create-workorder',
    name: 'Create Workorder',
    icon: 'plus-circle',
    table: 'clients',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 2,
    config: {
      route: (row: Record<string, unknown>) => workorderCreateRoute(row.id ?? row.key),
    },
  },
  {
    id: 'client-map-this-route',
    name: 'Map this Route',
    icon: 'map',
    table: 'clients',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 3,
    condition: (row) => Boolean(row.route), // Only show if client has a route
    config: {
      // Navigate to map with routeId filter - shows only this route
      route: (row: Record<string, unknown>) => `/client-map?routeId=${encodeURIComponent(String(row.route || ''))}`,
    },
  },
  // REMOVED: Duplicate inline actions - keeping only prominent versions above
  // client-log-service-inline, client-create-workorder-inline, client-view-map removed
  // to avoid duplicate buttons in client detail header
  // REMOVED: Add Note button from header - now handled inline in Notes section
  // {
  //   id: 'client-add-note',
  //   name: 'Add Note',
  //   ...
  // },
  // REMOVED: Request Reprice from header - now handled inline near monthly amount field
  // {
  //   id: 'client-request-reprice',
  //   ...
  // },
  {
    id: 'client-generate-report',
    name: 'Generate Report',
    icon: 'file-alt',
    table: 'clients',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 21,
    requiredViews: ['management', 'workorder-manager'],
    config: {
      route: '/reports/client',
      params: { clientId: 'key' },
    },
  },
  // REMOVED: Request QA from header - now handled in QA section only
  // {
  //   id: 'client-request-qa',
  //   ...
  // },

  // =============================================================================
  // CALL / SMS ACTIONS
  // =============================================================================
  {
    id: 'client-call-phone',
    name: 'Call Phone',
    icon: 'phone',
    table: 'clients',
    type: 'CALL',
    prominence: 'inline',
    order: 4,
    condition: (row) => Boolean(row.contactPhone),
    config: {
      phoneColumn: 'contactPhone',
    },
  },
  {
    id: 'client-send-sms',
    name: 'Send SMS',
    icon: 'comment',
    table: 'clients',
    type: 'SMS',
    prominence: 'hidden',
    order: 5,
    condition: () => false, // Hidden in AppSheet
    config: {
      phoneColumn: 'contactPhone',
    },
  },

  // =============================================================================
  // NAVIGATE URL ACTIONS (External Links)
  // =============================================================================
  {
    id: 'client-edit-inventory',
    name: 'Edit Inventory',
    icon: 'edit',
    table: 'clients',
    type: 'NAVIGATE_URL',
    prominence: 'prominent',
    order: 9,
    requiredViews: ['management', 'warehouse'],
    config: {
      url: (row) =>
        `https://docs.google.com/spreadsheets/d/${row.inventorySheetId || ''}/edit`,
    },
  },
  // REMOVED: Create New QA from header - now handled in QA section only
  // {
  //   id: 'client-create-new-qa',
  //   ...
  // },
  {
    id: 'client-view-inventory',
    name: 'View Inventory',
    icon: 'boxes',
    table: 'clients',
    type: 'NAVIGATE_URL',
    prominence: 'prominent',
    order: 14,
    condition: (row) => Boolean(row.inventory),
    config: {
      url: (row) =>
        `https://docs.google.com/spreadsheets/d/${row.inventorySheetId || ''}/view`,
    },
  },
  {
    id: 'client-view-inventory-android',
    name: 'View Inventory (Android)',
    icon: 'boxes',
    table: 'clients',
    type: 'NAVIGATE_URL',
    prominence: 'prominent',
    order: 15,
    condition: (row) => Boolean(row.inventory),
    config: {
      url: (row) =>
        `https://docs.google.com/spreadsheets/d/${row.inventorySheetId || ''}/view`,
    },
  },
  {
    id: 'client-view-inventory-inline',
    name: 'View Inventory',
    icon: 'boxes',
    table: 'clients',
    type: 'NAVIGATE_URL',
    prominence: 'inline',
    order: 16,
    condition: (row) => Boolean(row.inventory),
    config: {
      url: (row) =>
        `https://docs.google.com/spreadsheets/d/${row.inventorySheetId || ''}/view`,
    },
  },
  {
    id: 'client-view-inventory-android-inline',
    name: 'View Inventory (Android)',
    icon: 'boxes',
    table: 'clients',
    type: 'NAVIGATE_URL',
    prominence: 'inline',
    order: 17,
    condition: (row) => Boolean(row.inventory),
    config: {
      url: (row) =>
        `https://docs.google.com/spreadsheets/d/${row.inventorySheetId || ''}/view`,
    },
  },

  // =============================================================================
  // EMAIL ACTIONS
  // =============================================================================
  {
    id: 'client-send-scheduling-notification',
    name: 'Send Client Scheduling Notification',
    icon: 'envelope',
    table: 'clients',
    type: 'EMAIL',
    prominence: 'inline',
    order: 22,
    requiredViews: ['management'],
    condition: (row) => row.notifications === 'Yes',
    config: {
      to: (row) => String(row.contactEmail || ''),
      subject: (row) =>
        `Scheduling Notification - ${row.clientName || ''}`,
      body: (row) =>
        `Dear ${row.contactName || 'Client'},\n\nThis is a notification regarding the scheduling of services for ${row.clientName || 'your account'}.\n\nPlease let us know if you have any questions.\n\nBest regards,\nThe Wright Gardner`,
    },
  },

  // =============================================================================
  // ADD RECORD / EDIT RECORD ACTIONS
  // =============================================================================
  {
    id: 'client-edit',
    name: 'Edit',
    icon: 'edit',
    table: 'clients',
    type: 'EDIT_RECORD',
    prominence: 'prominent',
    order: 10,
    config: {},
  },
  {
    id: 'client-add',
    name: 'Add',
    icon: 'plus',
    table: 'clients',
    type: 'ADD_RECORD',
    prominence: 'prominent',
    order: 11,
    requiredViews: ['management'],
    config: {},
  },
  {
    id: 'client-cover-service-wo',
    name: 'Cover Service WO',
    icon: 'plus-square',
    table: 'clients',
    type: 'ADD_RECORD_TO',
    prominence: 'menu',
    order: 20,
    requiredViews: ['management', 'workorder-manager'],
    condition: (row) => Boolean(row.hasAgingService), // Client has aging service
    config: {
      targetTable: 'workorders',
      mapping: {
        clientId: 'key',
        clientName: 'clientName',
        mapAddress: 'mapAddress',
        tech: 'tech',
      },
    },
  },

  // =============================================================================
  // DELETE ACTIONS
  // =============================================================================
  {
    id: 'client-delete',
    name: 'Delete',
    icon: 'trash',
    table: 'clients',
    type: 'DELETE_RECORD',
    prominence: 'menu',
    order: 24,
    requiredViews: ['management'],
    needsConfirmation: true,
    confirmationMessage: 'Are you sure you want to delete this client? This action cannot be undone.',
    config: {},
  },
];
